import React, { forwardRef, useImperativeHandle, useEffect } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { List, ListItem } from '@mui/material';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { TextFieldCustom } from '../helpers/formFields';
import { formatString } from './valueFunctions';
import { formatNumberThsndAndDec } from '../helpers/stringFormat';


const AddProductTypeForm = forwardRef(({ productTypes, queryResult, formActions, formResultActions, onSubmit, onSubmitResult }, ref) => {

    const initialValues = {
        id: null,
        product_type: '',
        wish: '',
      };

    const productTypeKeys = Object.keys(queryResult?.product_type || {});
    const firstKey = productTypeKeys.length > 0 ? productTypeKeys[0] : '';

    const queryValues = {
        id: queryResult?.id,
        product_type: firstKey || '',
        annual_revenue: firstKey 
          ? (queryResult.product_type[firstKey]?.annual_revenue ?? { currency: '€', lower: '', higher: '' })
          : { currency: '€', lower: '', higher: '' },
        contribution_margin: firstKey 
          ? (queryResult.product_type[firstKey]?.contribution_margin ?? { unit: '%', lower: '', higher: '' })
          : { unit: '%', lower: '', higher: '' },
        development_costs: firstKey 
          ? (queryResult.product_type[firstKey]?.development_costs ?? { currency: '€', lower: '', higher: '' })
          : { currency: '€', lower: '', higher: '' },
        product_life_cycle_duration: firstKey 
          ? (queryResult.product_type[firstKey]?.product_life_cycle_duration ?? { unit: 'years', lower: '', higher: '' })
          : { unit: 'years', lower: '', higher: '' },
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const isLarger = (value1, value2) => {
        return parseFloat(value2.toString().replace(/,/g, '')) > parseFloat(value1.toString().replace(/,/g, ''));
    };

    const validate = (values) => {
        const errors = {};

        if (!values.product_type) {
            errors.product_type = 'Product Type is needed';
        }

        return errors
    }

    const validateResult = (values) => {
        const errors = {};

        if (!values.product_type) {
            errors.product_type = 'Product Type is needed';
        }

        if (Object.keys(queryResult).length > 0) {
        //     queryResult.cost_type[firstKey].multiple_type.forEach((type) => {
        //         // Check if values.cost_per_multiple exists and has a value for this type.
                if (!values.annual_revenue || !values.contribution_margin || !values.development_costs || !values.product_life_cycle_duration) {
                    return;
                }

                // Ensure we have an error object for this type.
                errors.annual_revenue = errors.annual_revenue || {};
                errors.contribution_margin = errors.contribution_margin || {};
                errors.development_costs = errors.development_costs || {};
                errors.product_life_cycle_duration = errors.product_life_cycle_duration || {};

                // Annual Revenue
                if (!isNumeric(values.annual_revenue.lower)) {
                    errors.annual_revenue.lower = 'Must be numeric value';
                } else if (!isPositive(values.annual_revenue.lower)) {
                    errors.annual_revenue.lower = 'Must be positive or zero';
                }

                if (!isNumeric(values.annual_revenue.higher)) {
                    errors.annual_revenue.higher = 'Must be numeric value';
                } else if (!isPositive(values.annual_revenue.higher)) {
                    errors.annual_revenue.higher = 'Must be positive';
                } else if (!isLarger(values.annual_revenue.lower, values.annual_revenue.higher)) {
                    errors.annual_revenue.higher = 'Must be higher than lower range';
                }

                // Contribution Margin
                if (!isNumeric(values.contribution_margin.lower)) {
                    errors.contribution_margin.lower = 'Must be numeric value';
                } else if (!isPositive(values.contribution_margin.lower)) {
                    errors.contribution_margin.lower = 'Must be positive or zero';
                }

                if (!isNumeric(values.contribution_margin.higher)) {
                    errors.contribution_margin.higher = 'Must be numeric value';
                } else if (!isPositive(values.contribution_margin.higher)) {
                    errors.contribution_margin.higher = 'Must be positive';
                } else if (!isLarger(values.contribution_margin.lower, values.contribution_margin.higher)) {
                    errors.contribution_margin.higher = 'Must be higher than lower range';
                }                

                // Development Costs
                if (!isNumeric(values.development_costs.lower)) {
                    errors.development_costs.lower = 'Must be numeric value';
                } else if (!isPositive(values.development_costs.lower)) {
                    errors.development_costs.lower = 'Must be positive or zero';
                }

                if (!isNumeric(values.development_costs.higher)) {
                    errors.development_costs.higher = 'Must be numeric value';
                } else if (!isPositive(values.development_costs.higher)) {
                    errors.development_costs.higher = 'Must be positive';
                } else if (!isLarger(values.development_costs.lower, values.development_costs.higher)) {
                    errors.development_costs.higher = 'Must be higher than lower range';
                }                

                // PLC Duration
                if (!isNumeric(values.product_life_cycle_duration.lower)) {
                    errors.product_life_cycle_duration.lower = 'Must be numeric value';
                } else if (!isPositive(values.product_life_cycle_duration.lower)) {
                    errors.product_life_cycle_duration.lower = 'Must be positive or zero';
                }

                if (!isNumeric(values.product_life_cycle_duration.higher)) {
                    errors.product_life_cycle_duration.higher = 'Must be numeric value';
                } else if (!isPositive(values.product_life_cycle_duration.higher)) {
                    errors.product_life_cycle_duration.higher = 'Must be positive';
                } else if (!isLarger(values.product_life_cycle_duration.lower, values.product_life_cycle_duration.higher)) {
                    errors.product_life_cycle_duration.higher = 'Must be higher than lower range';
                }                

        //     });

        //     if (errors.cost_per_multiple) {
        //         Object.keys(errors.cost_per_multiple).forEach(key => {
        //             if (Object.keys(errors.cost_per_multiple[key]).length === 0) {
        //                 delete errors.cost_per_multiple[key];
        //             }
        //         });

                if (Object.keys(errors.annual_revenue).length === 0) {
                    delete errors.annual_revenue;
                }
                if (Object.keys(errors.contribution_margin).length === 0) {
                    delete errors.contribution_margin;
                }
                if (Object.keys(errors.development_costs).length === 0) {
                    delete errors.development_costs;
                }
                if (Object.keys(errors.product_life_cycle_duration).length === 0) {
                    delete errors.product_life_cycle_duration;
                }
        //     }
        }


        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    const formikResult = useFormik({
        initialValues: queryValues,
        validate: validateResult,
        onSubmit: onSubmitResult,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
        },
    }));

    useEffect(() => {
        if (Object.keys(queryResult).length > 0) {
            let annual_revenue = { 'currency': '€', 'lower': '', 'higher': '' }
            let development_costs = { 'currency': '€', 'lower': '', 'higher': '' }
            if (firstKey && queryResult.product_type[firstKey]?.annual_revenue && queryResult.product_type[firstKey]?.development_costs) {
                annual_revenue = { ...queryResult.product_type[firstKey].annual_revenue }
                development_costs = { ...queryResult.product_type[firstKey]?.development_costs }

                annual_revenue = {
                    ...annual_revenue,
                    currency: annual_revenue.currency,
                    lower: formatNumberThsndAndDec(annual_revenue.lower || ''),
                    higher: formatNumberThsndAndDec(annual_revenue.higher || ''),
                }

                development_costs = {
                    ...development_costs,
                    currency: development_costs.currency,
                    lower: formatNumberThsndAndDec(development_costs.lower || ''),
                    higher: formatNumberThsndAndDec(development_costs.higher || ''),
                }
            }

            formikResult.setFieldValue("id", queryResult?.id || 0)
            formikResult.setFieldValue("product_type", firstKey || '')
            formikResult.setFieldValue("annual_revenue", annual_revenue)
            formikResult.setFieldValue("contribution_margin", firstKey ? queryResult.product_type[firstKey]?.contribution_margin : { 'unit': '%', 'lower': '', 'higher': '' })
            formikResult.setFieldValue("development_costs", development_costs)
            formikResult.setFieldValue("product_life_cycle_duration", firstKey ? queryResult.product_type[firstKey]?.product_life_cycle_duration : { 'unit': 'years', 'lower': '', 'higher': '' })

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryResult])

    if (Object.keys(queryResult).length > 0 && formikResult.values.product_type) {
        return (
            <Box component="form" onSubmit={formikResult.handleSubmit}>
                <Grid container columnGap={1}>
                    <Grid item xs={8}>
                        <Grid container rowGap={2} columnGap={1}>
                            <Grid item xs={6}>
                                <Typography variant="caption">
                                    Name Product Type
                                </Typography>
                                <TextFieldCustom
                                    name="product_type"
                                    value={formikResult.values.product_type}
                                    onChange={formikResult.handleChange}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, "product_type") && getIn(formikResult.touched, "product_type")) && Boolean(getIn(formikResult.errors, "product_type"))}
                                    helperText={getIn(formikResult.touched, "product_type") && getIn(formikResult.errors, "product_type")}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={4} />

                            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    Annual Revenue
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="caption">
                                    Lower Range
                                </Typography>
                                <TextFieldCustom
                                    name={'annual_revenue.lower'}
                                    startAdornment={formikResult.values.annual_revenue.currency}
                                    value={formikResult.values.annual_revenue.lower}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formikResult.setFieldValue('annual_revenue.lower', formattedValue || '')
                                    }}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, 'annual_revenue.lower') && getIn(formikResult.touched, "annual_revenue.lower")) && Boolean(getIn(formikResult.errors, 'annual_revenue.lower'))}
                                    helperText={getIn(formikResult.touched, 'annual_revenue.lower') && getIn(formikResult.errors, 'annual_revenue.lower')}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="caption">
                                    Higher Range
                                </Typography>
                                <TextFieldCustom
                                    name={'annual_revenue.higher'}
                                    startAdornment={formikResult.values.annual_revenue.currency}
                                    value={formikResult.values.annual_revenue.higher}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formikResult.setFieldValue('annual_revenue.higher', formattedValue || '')
                                    }}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, 'annual_revenue.higher') && getIn(formikResult.touched, "annual_revenue.higher")) && Boolean(getIn(formikResult.errors, 'annual_revenue.higher'))}
                                    helperText={getIn(formikResult.touched, 'annual_revenue.higher') && getIn(formikResult.errors, 'annual_revenue.higher')}
                                />
                            </Grid>

                            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    Contribution Margin
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="caption">
                                    Lower Range
                                </Typography>
                                <TextFieldCustom
                                    name={'contribution_margin.lower'}
                                    endAdornment={
                                        <Typography variant="caption">
                                            {formikResult.values.contribution_margin.unit}
                                        </Typography>
                                    }
                                    value={formikResult.values.contribution_margin.lower}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formikResult.setFieldValue('contribution_margin.lower', formattedValue || '')
                                    }}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, 'contribution_margin.lower') && getIn(formikResult.touched, "contribution_margin.lower")) && Boolean(getIn(formikResult.errors, 'contribution_margin.lower'))}
                                    helperText={getIn(formikResult.touched, 'contribution_margin.lower') && getIn(formikResult.errors, 'contribution_margin.lower')}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="caption">
                                    Higher Range
                                </Typography>
                                <TextFieldCustom
                                    name={'contribution_margin.higher'}
                                    endAdornment={
                                        <Typography variant="caption">
                                            {formikResult.values.contribution_margin.unit}
                                        </Typography>
                                    }
                                    value={formikResult.values.contribution_margin.higher}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formikResult.setFieldValue('contribution_margin.higher', formattedValue || '')
                                    }}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, 'contribution_margin.higher') && getIn(formikResult.touched, "contribution_margin.higher")) && Boolean(getIn(formikResult.errors, 'contribution_margin.higher'))}
                                    helperText={getIn(formikResult.touched, 'contribution_margin.higher') && getIn(formikResult.errors, 'contribution_margin.higher')}
                                />
                            </Grid>

                            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    Development Costs
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="caption">
                                    Lower Range
                                </Typography>
                                <TextFieldCustom
                                    name={'development_costs.lower'}
                                    startAdornment={formikResult.values.development_costs.currency}
                                    value={formikResult.values.development_costs.lower}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formikResult.setFieldValue('development_costs.lower', formattedValue || '')
                                    }}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, 'development_costs.lower') && getIn(formikResult.touched, "development_costs.lower")) && Boolean(getIn(formikResult.errors, 'development_costs.lower'))}
                                    helperText={getIn(formikResult.touched, 'development_costs.lower') && getIn(formikResult.errors, 'development_costs.lower')}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="caption">
                                    Higher Range
                                </Typography>
                                <TextFieldCustom
                                    name={'development_costs.higher'}
                                    startAdornment={formikResult.values.development_costs.currency}
                                    value={formikResult.values.development_costs.higher}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formikResult.setFieldValue('development_costs.higher', formattedValue || '')
                                    }}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, 'development_costs.higher') && getIn(formikResult.touched, "development_costs.higher")) && Boolean(getIn(formikResult.errors, 'development_costs.higher'))}
                                    helperText={getIn(formikResult.touched, 'development_costs.higher') && getIn(formikResult.errors, 'development_costs.higher')}
                                />
                            </Grid>

                            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    Product Life Cycle Duration
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="caption">
                                    Lower Range
                                </Typography>
                                <TextFieldCustom
                                    name={'product_life_cycle_duration.lower'}
                                    endAdornment={
                                        <Typography variant="caption">
                                            {formikResult.values.product_life_cycle_duration.unit}
                                        </Typography>
                                    }
                                    value={formikResult.values.product_life_cycle_duration.lower}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formikResult.setFieldValue('product_life_cycle_duration.lower', formattedValue || '')
                                    }}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, 'product_life_cycle_duration.lower') && getIn(formikResult.touched, "product_life_cycle_duration.lower")) && Boolean(getIn(formikResult.errors, 'product_life_cycle_duration.lower'))}
                                    helperText={getIn(formikResult.touched, 'product_life_cycle_duration.lower') && getIn(formikResult.errors, 'product_life_cycle_duration.lower')}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="caption">
                                    Higher Range
                                </Typography>
                                <TextFieldCustom
                                    name={'product_life_cycle_duration.higher'}
                                    endAdornment={
                                        <Typography variant="caption">
                                            {formikResult.values.product_life_cycle_duration.unit}
                                        </Typography>
                                    }
                                    value={formikResult.values.product_life_cycle_duration.higher}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formikResult.setFieldValue('product_life_cycle_duration.higher', formattedValue || '')
                                    }}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, 'product_life_cycle_duration.higher') && getIn(formikResult.touched, "product_life_cycle_duration.higher")) && Boolean(getIn(formikResult.errors, 'product_life_cycle_duration.higher'))}
                                    helperText={getIn(formikResult.touched, 'product_life_cycle_duration.higher') && getIn(formikResult.errors, 'product_life_cycle_duration.higher')}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                {formResultActions()}
                            </Grid>
                        </Grid>
                    </Grid>
                    {/* <Grid item xs={3}>
                        <Paper elevation={3} sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Description:
                            </Typography>
                            <Typography variant="caption" sx={{ mb: 1 }}>
                                {queryResult?.cost_type[Object.keys(queryResult?.cost_type)[0]]?.description}
                            </Typography>
                        </Paper>
                    </Grid> */}
                </Grid>
            </Box>
        )
    }

    return (
        <Box component="form" onSubmit={formik.handleSubmit}>
            <Grid container columnGap={1}>
                <Grid item xs={8}>
                    <Grid container rowGap={2} columnGap={1}>
                        <Grid item xs={6}>
                            <Typography variant="caption">
                                Input Product Type to request
                            </Typography>
                            <TextFieldCustom
                                name="product_type"
                                value={formik.values.product_type}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={(getIn(formik.errors, "product_type") && getIn(formik.touched, "product_type")) && Boolean(getIn(formik.errors, "product_type"))}
                                helperText={getIn(formik.touched, "product_type") && getIn(formik.errors, "product_type")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4} />
                        <Grid item xs={12}>
                            <Typography variant="caption">
                                Specify a wish if you would like to put your request in some context
                            </Typography>
                            <TextFieldCustom
                                name="wish"
                                value={formik.values.wish}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "wish") && Boolean(getIn(formik.errors, "wish"))}
                                helperText={getIn(formik.touched, "wish") && getIn(formik.errors, "wish")}
                                fullWidth
                            />
                            <Typography variant="caption">
                                E.g. "Large company of type X producing products of type Y"
                            </Typography>
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, mb: 4 }}>
                        {formActions()}
                    </Box>
                </Grid>

                <Grid item xs={3}>
                    <Paper elevation={3} sx={{ p: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Existing Product Types:
                        </Typography>
                        <List sx={{ padding: 0, listStyleType: 'disc', listStylePosition: 'inside' }}>
                            {Object.keys(productTypes).map((product_type, index) => (
                                <ListItem
                                    key={index}
                                    sx={{
                                        display: 'list-item',
                                        paddingY: 0.5,
                                        paddingLeft: 2,
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 'normal' }}>
                                        {formatString(product_type)}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>

            </Grid>
        </Box>
    )
})

export default AddProductTypeForm
